/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useContext} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import {clearComponentStyleProperty, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import type {ColorControlDescriptor} from './color-control.types';
import {getDisplayHex, getFormattedColor} from './color-control.library';
import {useAppDispatch} from '@/hooks';
import styles from './color-control.module.scss';

interface ColorControlInnerProps extends EmailMakerControlProps {
  descriptor: ColorControlDescriptor;
}

function ColorControlInner({component, descriptor}: ColorControlInnerProps): ReactElement {
  const {editor, actions, state} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();

  const rawValue = useStyleProperty(editor, component, descriptor.property);

  const currentHex = getFormattedColor(rawValue);
  const isDeletable = descriptor.isDeletable !== false;

  function handleClear(): void {
    if (!editor) {
      return;
    }
    clearComponentStyleProperty(editor, component, descriptor.property);
  }

  // The COLOR_PICKER overlay only mounts inside the desktop right panel; tablet/mobile open the equivalent bottom sheet.
  function openColorPicker(): void {
    if (state.editorViewport === EditorViewport.DESKTOP) {
      actions.openRightPanelColorPicker({component, descriptor});
      return;
    }
    actions.setColorPickerTarget({component, descriptor});
    dispatch(openPanelInModal({modalId: MODALS_IDS.EMAIL_MAKER_COLOR_PICKER_SHEET, panelProps: {}}));
  }

  const displayHex = getDisplayHex(rawValue, descriptor);

  function renderClearButton(): ReactElement | null {
    if (!isDeletable) {
      return null;
    }
    return (
      <div className={`${styles.clearBtn} ${currentHex ? '' : styles.clearBtnHidden}`}>
        <Icon ariaLabel={window.i18next.t('pmwjs_remove_color')} icon="icon-delete" isUsedAsButton onClick={handleClear} size={IconSize.SIZE_ICON_16} />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelRow}>
        <Text val={window.i18next.t(descriptor.labelKey)} size={TextSize.MD} className={styles.label} />
        <div className={styles.controls}>
          {renderClearButton()}
          <button type="button" className={styles.colorSwatch} style={{backgroundColor: displayHex}} aria-label={window.i18next.t(descriptor.labelKey)} onClick={openColorPicker} />
        </div>
      </div>
      {renderNote(descriptor.noteKey)}
    </div>
  );
}

function renderNote(noteKey: string | undefined): ReactElement | null {
  if (!noteKey) {
    return null;
  }
  return <Text val={window.i18next.t(noteKey)} size={TextSize.SM} className={styles.note} />;
}

export function makeColorControl(descriptor: ColorControlDescriptor): EmailMakerControl {
  return createDescriptorControl(ColorControlInner, descriptor);
}
